import React, { useState, useEffect } from "react";
import useAxios from "../../utils/useAxios";
import { showModal, hideModal } from "../../utils/modalUtility";

const createEmptyRow = () => ({
  id: Date.now() + Math.random(),
  caption: "",
  imageFile: null,
  previewUrl: "",
});

const DailyDarshan = () => {
  const axiosAPI = useAxios();

  const [mainTitle, setMainTitle] = useState("");
  const [darshanRows, setDarshanRows] = useState([createEmptyRow()]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentDarshanId, setCurrentDarshanId] = useState(null);
  const [darshanList, setDarshanList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const buildQuery = () => {
    const params = new URLSearchParams();
    params.append("page", page);
    return params.toString();
  };

  const fetchDailyDarshan = async () => {
    setLoading(true);
    const queryString = buildQuery();

    try {
      const res = await axiosAPI.get(
        `/api/daily-darshan/get-all?${queryString}`,
      );
      setDarshanList(res?.data?.results || []);
      setTotalPages(res?.data?.totalPages || 1);
    } catch (err) {
      setErrorMessage("Failed to fetch daily darshan records.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDailyDarshan();
  }, [page]);

  const resetForm = () => {
    darshanRows.forEach((row) => {
      if (row.previewUrl) {
        URL.revokeObjectURL(row.previewUrl);
      }
    });
    setMainTitle("");
    setDarshanRows([createEmptyRow()]);
    setIsEditMode(false);
    setCurrentDarshanId(null);
  };

  const openAddModal = () => {
    setErrorMessage("");
    setSuccessMessage("");
    resetForm();
    showModal("newDailyDarshanModal");
  };

  const openEditModal = (item) => {
    setErrorMessage("");
    setSuccessMessage("");

    const mediaItems = parseMediaItems(item?.media);
    const preparedRows =
      mediaItems.length > 0
        ? mediaItems.map((media) => ({
            id: Date.now() + Math.random(),
            caption: media?.caption || "",
            imageFile: null,
            previewUrl: media?.url || media?.media_url || "",
          }))
        : [createEmptyRow()];

    setMainTitle(item?.title || "");
    setDarshanRows(preparedRows);
    setIsEditMode(true);
    setCurrentDarshanId(item?.id || item?._id);
    showModal("newDailyDarshanModal");
  };

  const closeModal = () => {
    resetForm();
    hideModal("newDailyDarshanModal");
  };

  const addDarshanRow = () => {
    setDarshanRows((prev) => [...prev, createEmptyRow()]);
  };

  const removeDarshanRow = (rowId) => {
    setDarshanRows((prev) => {
      if (prev.length === 1) return prev;
      const target = prev.find((row) => row.id === rowId);
      if (target?.previewUrl) {
        URL.revokeObjectURL(target.previewUrl);
      }
      return prev.filter((row) => row.id !== rowId);
    });
  };

  const handleCaptionChange = (rowId, value) => {
    setDarshanRows((prev) =>
      prev.map((row) => (row.id === rowId ? { ...row, caption: value } : row)),
    );
  };

  const handleImageChange = (rowId, file) => {
    setErrorMessage("");
    setSuccessMessage("");

    if (!file) return;
    if (!file.type.startsWith("image/")) {
      //   setErrorMessage("Please upload valid image files only.");
      window.notifyMessage(
        "Error",
        "Please upload valid image files only.",
        "error",
      );
      return;
    }

    setDarshanRows((prev) =>
      prev.map((row) => {
        if (row.id !== rowId) return row;
        if (row.previewUrl) {
          URL.revokeObjectURL(row.previewUrl);
        }
        return {
          ...row,
          imageFile: file,
          previewUrl: URL.createObjectURL(file),
        };
      }),
    );
  };

  const validateRows = () => {
    if (!mainTitle.trim()) {
      //   setErrorMessage("Main title is required.");
      window.notifyMessage("Error", "Main title is required.", "error");
      return false;
    }
    if (darshanRows.length === 0) {
      //   setErrorMessage("Please add at least one caption and image.");
      window.notifyMessage(
        "Error",
        "Please add at least one caption and image.",
        "error",
      );
      return false;
    }
    for (let i = 0; i < darshanRows.length; i++) {
      const row = darshanRows[i];
      if (!row?.caption?.trim()) {
        // setErrorMessage(`Caption is required for row ${i + 1}.`);
        window.notifyMessage(
          "Error",
          `Caption is required for row ${i + 1}.`,
          "error",
        );
        return false;
      }
      if (!row?.imageFile && !row?.previewUrl) {
        // setErrorMessage(`Image is required for row ${i + 1}.`);
        window.notifyMessage(
          "Error",
          `Image is required for row ${i + 1}.`,
          "error",
        );
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage("");
    setSuccessMessage("");

    if (!validateRows()) {
      return;
    }

    const formData = new FormData();
    formData.append("title", mainTitle.trim());
    darshanRows.forEach((row, index) => {
      formData.append(`items[${index}][caption]`, row.caption.trim());
      if (row.imageFile) {
        formData.append(`items[${index}][media]`, row.imageFile);
      } else if (row.previewUrl) {
        formData.append(`items[${index}][existing_url]`, row.previewUrl);
      }
    });

    console.log("Submitting form with data:");
    for (let pair of formData.entries()) {
      console.log(pair[0] + ": ", pair[1]);
    }

    try {
      setIsSubmitting(true);
      const response = isEditMode
        ? await axiosAPI.put(
            `/api/daily-darshan/update/${currentDarshanId}`,
            formData,
            {
              headers: { "Content-Type": "multipart/form-data" },
            },
          )
        : await axiosAPI.post("/api/daily-darshan/add", formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });

      const apiMessage =
        response?.data?.message ||
        (isEditMode
          ? "Daily darshan updated successfully."
          : "Daily darshan added successfully.");
      setSuccessMessage(apiMessage);
      if (typeof window.notifyMessage === "function") {
        window.notifyMessage("Success", apiMessage, "success");
      }
      hideModal("newDailyDarshanModal");
      resetForm();
      fetchDailyDarshan();
    } catch (error) {
      const apiError =
        error?.response?.data?.message || "Failed to upload daily darshan.";
      //   setErrorMessage(apiError);
      window.notifyMessage("Error", apiError, "error");
      if (typeof window.notifyMessage === "function") {
        window.notifyMessage("Error", apiError, "error");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this item?")) return;

    setDeletingId(id);
    try {
      await axiosAPI.delete(`/api/daily-darshan/delete/${id}`);
      if (typeof window.notifyMessage === "function") {
        window.notifyMessage(
          "Success",
          "Daily darshan deleted successfully",
          "success",
        );
      }
      fetchDailyDarshan();
    } catch (error) {
      const apiError =
        error?.response?.data?.message || "Failed to delete daily darshan.";
      //   setErrorMessage(apiError);
      window.notifyMessage("Error", apiError, "error");
      if (typeof window.notifyMessage === "function") {
        window.notifyMessage("Error", apiError, "error");
      }
    } finally {
      setDeletingId(null);
    }
  };

  const handlePageClick = (pageNum) => {
    if (pageNum >= 1 && pageNum <= totalPages) {
      setPage(pageNum);
    }
  };

  const parseMediaItems = (media) => {
    if (Array.isArray(media)) {
      return media;
    }

    if (typeof media === "string") {
      try {
        const parsed = JSON.parse(media);
        return Array.isArray(parsed) ? parsed : [];
      } catch (error) {
        return [];
      }
    }

    return [];
  };

  return (
    <div className="container">
      <div className="page-inner">
        <div className="d-flex justify-content-end align-items-center flex-wrap gap-2 mb-4">
          <button
            type="button"
            className="btn btn-dark btn-round"
            onClick={openAddModal}
          >
            Add Daily Darshan
          </button>
        </div>

        {/* {errorMessage && (
          <div className="alert alert-danger" role="alert">
            {errorMessage}
          </div>
        )}

        {successMessage && (
          <div className="alert alert-success" role="status">
            {successMessage}
          </div>
        )} */}

        <div
          className="modal fade"
          id="newDailyDarshanModal"
          tabIndex="-1"
          aria-labelledby="newDailyDarshanModalLabel"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="newDailyDarshanModalLabel">
                  {isEditMode ? "Edit Daily Darshan" : "Add Daily Darshan"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                  onClick={closeModal}
                ></button>
              </div>

              <div className="modal-body">
                <form onSubmit={handleSubmit}>
                  <div className="mb-3">
                    <label className="form-label fw-bold">Main Title</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter main title for all darshan"
                      value={mainTitle}
                      onChange={(e) => setMainTitle(e.target.value)}
                      disabled={isSubmitting}
                    />
                  </div>
                  {darshanRows.map((row, index) => (
                    <div
                      key={row.id}
                      className="border rounded p-3 mb-3 bg-light"
                    >
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6 className="mb-0">Item {index + 1}</h6>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => removeDarshanRow(row.id)}
                          disabled={darshanRows.length === 1 || isSubmitting}
                        >
                          Remove
                        </button>
                      </div>
                      <div className="mb-3">
                        <label className="form-label">Caption</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Enter caption"
                          value={row.caption}
                          onChange={(event) =>
                            handleCaptionChange(row.id, event.target.value)
                          }
                          disabled={isSubmitting}
                        />
                      </div>
                      <div className="mb-3">
                        <label className="form-label">Image</label>
                        <input
                          type="file"
                          className="form-control"
                          accept="image/*"
                          onChange={(event) =>
                            handleImageChange(row.id, event.target.files?.[0])
                          }
                          disabled={isSubmitting}
                        />
                      </div>
                      {row.previewUrl && (
                        <div className="border rounded p-2 bg-white text-center">
                          <img
                            src={row.previewUrl}
                            alt={`Preview ${index + 1}`}
                            style={{ maxWidth: "100%", maxHeight: "220px" }}
                          />
                        </div>
                      )}
                    </div>
                  ))}

                  <div className="d-flex justify-content-between align-items-center">
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      onClick={addDarshanRow}
                      disabled={isSubmitting}
                    >
                      + Add More
                    </button>

                    <button
                      type="submit"
                      className="btn btn-primary btn-round"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          ></span>
                          Saving...
                        </>
                      ) : isEditMode ? (
                        "Update Daily Darshan"
                      ) : (
                        "Save Daily Darshan"
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-12">
            <div className="card">
              <div className="card-header">
                <h4 className="card-title mb-1">
                  All Daily Darshan ({darshanList.length})
                </h4>
                <p className="mb-0 text-muted">
                  Every record contains a main title and media items.
                </p>
              </div>

              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>Title</th>
                        <th>Media</th>
                        <th>Created</th>
                        <th>Action</th>
                      </tr>
                    </thead>

                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan={4} className="text-center">
                            <div
                              className="spinner-border text-success"
                              role="status"
                            >
                              <span className="visually-hidden">
                                Loading...
                              </span>
                            </div>
                          </td>
                        </tr>
                      ) : darshanList.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="text-center">
                            No Data Found
                          </td>
                        </tr>
                      ) : (
                        darshanList.map((item) => {
                          const mediaArr = parseMediaItems(item.media);

                          return (
                            <tr key={item.id || item._id}>
                              <td>{item.title || "-"}</td>
                              <td>
                                {mediaArr.length > 0 ? (
                                  <div
                                    style={{
                                      display: "flex",
                                      gap: 8,
                                      flexWrap: "wrap",
                                    }}
                                  >
                                    {mediaArr.map((media, index) => (
                                      <div
                                        key={`${item.id || item._id}-${index}`}
                                        style={{ textAlign: "center" }}
                                      >
                                        <img
                                          src={media?.url || media?.media_url}
                                          alt={
                                            media?.caption ||
                                            `darshan ${index + 1}`
                                          }
                                          style={{
                                            maxWidth: 90,
                                            maxHeight: 60,
                                            borderRadius: 6,
                                          }}
                                        />
                                        <div style={{ fontSize: 12 }}>
                                          {media?.caption || "-"}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <span className="text-muted">No Image</span>
                                )}
                              </td>
                              <td>
                                {item.created_at
                                  ? new Date(
                                      item.created_at,
                                    ).toLocaleDateString()
                                  : "-"}
                              </td>
                              <td>
                                <button
                                  className="btn btn-icon btn-round btn-dark btn-sm me-2"
                                  onClick={() => openEditModal(item)}
                                  disabled={
                                    deletingId === (item.id || item._id)
                                  }
                                >
                                  <i className="fas fa-edit"></i>
                                </button>
                                <button
                                  className="btn btn-icon btn-round btn-danger btn-sm"
                                  disabled={
                                    deletingId === (item.id || item._id)
                                  }
                                  onClick={() =>
                                    handleDelete(item.id || item._id)
                                  }
                                >
                                  {deletingId === (item.id || item._id) ? (
                                    <span className="spinner-border spinner-border-sm"></span>
                                  ) : (
                                    <i className="fas fa-trash"></i>
                                  )}
                                </button>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <nav aria-label="Page navigation">
              <ul className="pagination flex justify-content-end">
                <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(1)}
                  >
                    First
                  </button>
                </li>

                <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(page - 1)}
                  >
                    Previous
                  </button>
                </li>

                {(() => {
                  const pageNumbers = [];
                  const maxPagesToShow = 5;
                  const startPage =
                    Math.floor((page - 1) / maxPagesToShow) * maxPagesToShow +
                    1;
                  const endPage = Math.min(
                    startPage + maxPagesToShow - 1,
                    totalPages,
                  );

                  for (let i = startPage; i <= endPage; i++) {
                    pageNumbers.push(
                      <li
                        key={i}
                        className={`page-item ${page === i ? "active" : ""}`}
                      >
                        <button
                          className="page-link"
                          onClick={() => handlePageClick(i)}
                        >
                          {i}
                        </button>
                      </li>,
                    );
                  }
                  return pageNumbers;
                })()}

                <li
                  className={`page-item ${page === totalPages ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(page + 1)}
                  >
                    Next
                  </button>
                </li>

                <li
                  className={`page-item ${page === totalPages ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(totalPages)}
                  >
                    Last
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DailyDarshan;
